import { jsx as _jsx } from "react/jsx-runtime";
import React from "react";
import ReactDOM from "react-dom/client";
import { App } from "./App";
import { BrowserRouter } from "react-router-dom";
import { StyledEngineProvider, ThemeProvider } from "@mui/material";
import { theme } from "./util/theme";
import { WebgateProvider as _WebgateProvider } from "@xvia/webgate-connect";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./index.css";
const WebgateProvider = _WebgateProvider;
const ACCESSIBILITY_SETTINGS_STORAGE = "accessibility-settings";
const DEFAULT_FONT_SIZE = 16;
const MIN_FONT_SIZE = 12;
const MAX_FONT_SIZE = 24;
let lastAppliedContrast = null;
let lastAppliedFontDelta = null;
let lastSettingsRaw = null;
const parseAccessibilitySettings = (rawValue) => {
    if (!rawValue)
        return null;
    try {
        return JSON.parse(rawValue);
    }
    catch (_a) {
        return null;
    }
};
const normalizeFontSize = (fontSize) => {
    if (fontSize === null || fontSize === undefined)
        return DEFAULT_FONT_SIZE;
    const parsedValue = typeof fontSize === "number" ? fontSize : Number(fontSize);
    if (!Number.isFinite(parsedValue))
        return DEFAULT_FONT_SIZE;
    if (parsedValue < MIN_FONT_SIZE || parsedValue > MAX_FONT_SIZE) {
        return DEFAULT_FONT_SIZE;
    }
    return parsedValue;
};
const applyAccessibilitySettings = (contrast, fontSize) => {
    if (typeof window === "undefined")
        return;
    const nextContrast = contrast === "high" ? "high" : "normal";
    const normalizedFontSize = normalizeFontSize(fontSize);
    const nextFontDelta = normalizedFontSize - DEFAULT_FONT_SIZE;
    const shouldUpdateContrast = nextContrast !== lastAppliedContrast;
    const shouldUpdateFont = nextFontDelta !== lastAppliedFontDelta;
    if (!shouldUpdateContrast && !shouldUpdateFont)
        return;
    const root = document.documentElement;
    if (shouldUpdateContrast) {
        root.dataset.accessibilityContrast = nextContrast;
        lastAppliedContrast = nextContrast;
    }
    if (shouldUpdateFont) {
        if (nextFontDelta === 0) {
            root.style.removeProperty("--accessibility-font-delta");
        }
        else {
            root.style.setProperty("--accessibility-font-delta", `${nextFontDelta}px`);
        }
        lastAppliedFontDelta = nextFontDelta;
    }
};
const syncAccessibilityStorage = () => {
    if (typeof window === "undefined")
        return;
    try {
        const localValue = window.localStorage.getItem(ACCESSIBILITY_SETTINGS_STORAGE);
        if (localValue === lastSettingsRaw)
            return;
        lastSettingsRaw = localValue;
        const settings = parseAccessibilitySettings(localValue);
        applyAccessibilitySettings(settings === null || settings === void 0 ? void 0 : settings.contrast, settings === null || settings === void 0 ? void 0 : settings.fontSize);
    }
    catch (_a) {
        return;
    }
};
if (typeof window !== "undefined") {
    syncAccessibilityStorage();
    window.addEventListener("storage", (event) => {
        if (event.key === ACCESSIBILITY_SETTINGS_STORAGE) {
            syncAccessibilityStorage();
        }
    });
    window.addEventListener("focus", syncAccessibilityStorage);
    document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") {
            syncAccessibilityStorage();
        }
    });
    if (typeof MutationObserver !== "undefined") {
        const observer = new MutationObserver(() => {
            syncAccessibilityStorage();
        });
        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ["style", "data-accessibility-contrast"],
        });
    }
}
const baseName = window.__APP_BASE__ || "/";
const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(_jsx(React.StrictMode, { children: _jsx(BrowserRouter, Object.assign({ basename: baseName }, { children: _jsx(WebgateProvider, Object.assign({ portalUrl: process.env.PORTAL_URL }, { children: _jsx(ThemeProvider, Object.assign({ theme: theme }, { children: _jsx(StyledEngineProvider, Object.assign({ injectFirst: true }, { children: _jsx(App, {}) })) })) })) })) }));
